import React from 'react';
import PropTypes from 'prop-types';
import EditorField from '../Editor/editor-field';
import { parseValue, checkValueType, parseNumber } from '../Editor/util';
import { InputV2 } from '../v2';
import { InputShape } from '../v2/input';

// It should render a dropdown or input, based on the value type
// It should render a list of options
// Clicking the option should set the value types
// The dropdown should support attributes quoted in double curly braces

const getValueTypes = (type) => {
  switch (type) {
    case 'EQUAL':
    case 'NOT_EQUAL':
      return ['string', 'boolean', 'attribute', 'number', 'channel'];
    case 'CONTAINS':
      return ['string', 'attribute'];
    case 'IS_IN':
    case 'IN':
      return ['list', 'attribute'];
    default:
      return ['attribute', 'number'];
  }
};

function PolicyConditionValue(props) {
  const {
    rowItem,
    rowIndex,
    isEditing,
    temporaryPolicy,
    customAttributes,
    intents,
    channels,
    validateObject,
  } = props;

  const valueType = checkValueType(rowItem.value);

  const isAttribute = valueType === 'attribute';
  const isList = valueType === 'list';
  const isBoolean = valueType === 'boolean';
  const isNumber = valueType === 'number';
  const isChannel = valueType === 'channel';
  const isErrored = rowItem.errorArray && rowItem.errorArray.includes('value');

  const selectItem = (item, type) => {
    const newObject = { ...temporaryPolicy };

    if (typeof item === 'object') {
      newObject.conditions[rowIndex].value = item.name;
    } else if (type === 'number') {
      newObject.conditions[rowIndex].value = parseNumber(item);
    } else {
      newObject.conditions[rowIndex].value = item;
    }

    validateObject(newObject);
  };

  const selectValueType = (type) => {
    const newPolicy = { ...temporaryPolicy };
    switch (type) {
      case 'boolean':
        newPolicy.conditions[rowIndex].value = true;
        break;
      case 'attribute':
        newPolicy.conditions[rowIndex].value = '{{}}';
        break;
      case 'list':
        newPolicy.conditions[rowIndex].value = '[]';
        break;
      case 'number':
        newPolicy.conditions[rowIndex].value = 0;
        break;
      case 'channel':
        newPolicy.conditions[rowIndex].value = '%%%%';
        break;
      default:
        newPolicy.conditions[rowIndex].value = '';
        break;
    }
    validateObject(newPolicy);
  };

  const getPlaceholder = () => {
    switch (valueType) {
      case 'list':
        return '"Item 1", "Item 2", etc';
      case 'attribute':
        return 'Choose an attribute';
      case 'channel':
        return 'Choose a channel';
      default:
        return 'Enter a value';
    }
  };

  const getItems = (type, value) => {
    switch (type) {
      case 'boolean':
        return [true, false].filter((item) => item !== value);
      case 'string':
        return intents.filter((item) => item.name !== value);
      case 'channel':
        return channels.filter((item) => item !== value);
      default:
        return customAttributes.filter((item) => item !== value);
    }
  };

  const value = parseValue(valueType, rowItem.value);

  const component = (isEditing && (isList || isNumber))
    ? (
      <InputV2
        isErrored={isErrored}
        value={value}
        placeholder={getPlaceholder(rowItem.value)}
        onChange={(event) => selectItem(isList ? `[${event.target.value}]` : event.target.value, valueType)}
        shape={InputShape.SQUARE}
        testId="editor-text"
      />
    ) : (
      <EditorField
        isEditing={isEditing}
        value={value}
        selectItem={isChannel ? (name) => selectItem(`%%${name}%%`, valueType) : (name) => selectItem(isAttribute ? `{{${name}}}` : name, valueType)}
        items={getItems(valueType, value)}
        actionType={valueType}
        noFreeText={isBoolean}
        isErrored={isErrored}
        placeholder={getPlaceholder(rowItem.value)}
      />
    );

  return (
    <>
      {isEditing && (getValueTypes(rowItem.type, valueType).length) && (
        <EditorField
          isEditing
          value={valueType}
          selectItem={(type) => selectValueType(type)}
          items={getValueTypes(rowItem.type, valueType)}
          noFreeText
        />
      )}
      {component}
    </>
  );
}

PolicyConditionValue.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  temporaryPolicy: PropTypes.object.isRequired,
  customAttributes: PropTypes.array.isRequired,
  intents: PropTypes.array.isRequired,
  channels: PropTypes.array.isRequired,
  rowItem: PropTypes.object.isRequired,
  rowIndex: PropTypes.number.isRequired,
  validateObject: PropTypes.func.isRequired,
};

export default PolicyConditionValue;
